import { SchemaField } from '@/lib/data-standards/schemas';

interface GeneratorOptions {
  standardName: string;
  standardVersion: string;
  selectedFields: string[];
  requiredOverrides: Record<string, boolean>;
}

function toPascalCase(str: string): string {
  return str
    .replace(/[^a-zA-Z0-9]+/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join('');
}

function schemaPropertyToTsType(
  prop: Record<string, unknown>,
  name: string,
  nestedInterfaces: string[]
): string {
  // Handle enum
  if (prop.enum) {
    return (prop.enum as string[]).map(v => `'${v}'`).join(' | ');
  }

  const type = prop.type as string;

  switch (type) {
    case 'string':
      return 'string';
    case 'number':
    case 'integer':
      return 'number';
    case 'boolean':
      return 'boolean';
    case 'array': {
      const items = prop.items as Record<string, unknown>;
      if (items?.type === 'object' && items?.properties) {
        const nestedName = toPascalCase(name) + 'Item';
        generateNestedInterface(items, nestedName, nestedInterfaces);
        return `${nestedName}[]`;
      }
      if (items?.enum) {
        const enumType = (items.enum as string[]).map(v => `'${v}'`).join(' | ');
        return `(${enumType})[]`;
      }
      if (items) {
        return `${schemaPropertyToTsType(items, name, nestedInterfaces)}[]`;
      }
      return 'unknown[]';
    }
    case 'object':
      if (prop.properties) {
        const nestedName = toPascalCase(name);
        generateNestedInterface(prop, nestedName, nestedInterfaces);
        return nestedName;
      }
      return 'Record<string, unknown>';
    default:
      return 'unknown';
  }
}

function generateNestedInterface(
  prop: Record<string, unknown>,
  interfaceName: string,
  nestedInterfaces: string[]
): void {
  const properties = prop.properties as Record<string, Record<string, unknown>>;
  const required = (prop.required as string[]) || [];

  if (!properties) return;

  const lines: string[] = [];
  lines.push(`export interface ${interfaceName} {`);

  Object.entries(properties).forEach(([propName, propDef]) => {
    const isRequired = required.includes(propName);
    const optionalMarker = isRequired ? '' : '?';
    const description = propDef.description as string;

    // Check for nested objects/arrays within this nested interface
    const tsType = schemaPropertyToTsType(propDef, propName, nestedInterfaces);

    if (description) {
      lines.push(`  /** ${description} */`);
    }
    lines.push(`  ${propName}${optionalMarker}: ${tsType};`);
  });

  lines.push('}');
  lines.push('');

  // Add to beginning so nested types are defined before they're used
  nestedInterfaces.unshift(lines.join('\n'));
}

export function generateTypeScript(
  schema: object,
  fields: SchemaField[],
  options: GeneratorOptions
): string {
  const schemaObj = schema as Record<string, unknown>;
  const properties = (schemaObj.properties || {}) as Record<string, Record<string, unknown>>;
  const required = (schemaObj.required as string[]) || [];

  const lines: string[] = [
    `// Generated from AutomotiveAPI ${options.standardName} v${options.standardVersion}`,
    `// Selected fields: ${options.selectedFields.join(', ')}`,
    `// Generated on: ${new Date().toISOString().split('T')[0]}`,
    '',
  ];

  const nestedInterfaces: string[] = [];
  const interfaceFields: string[] = [];

  options.selectedFields.forEach(fieldName => {
    const prop = properties[fieldName];
    if (!prop) return;

    const isRequired = fieldName in options.requiredOverrides
      ? options.requiredOverrides[fieldName]
      : required.includes(fieldName);
    const tsType = schemaPropertyToTsType(prop, fieldName, nestedInterfaces);
    const optionalMarker = isRequired ? '' : '?';
    const description = prop.description as string;

    if (description) {
      interfaceFields.push(`  /** ${description} */`);
    }
    interfaceFields.push(`  ${fieldName}${optionalMarker}: ${tsType};`);
  });

  // Add nested interfaces first
  if (nestedInterfaces.length > 0) {
    lines.push(...nestedInterfaces);
  }

  // Add main interface
  const interfaceName = toPascalCase(options.standardName) + 'Subset';
  lines.push(`export interface ${interfaceName} {`);
  lines.push(...interfaceFields);
  lines.push('}');

  return lines.join('\n');
}
